'use client';

import { useEffect, useState, useMemo, useRef } from 'react';
import Fuse from 'fuse.js';
import { Button } from '../button/Button';
import Icon from '@/components/icon/Icon';
import Card from '@/components/card/Card';
import { getContentHubPath } from '@/utils/get-content-hub-path';
import { fetchSearchData } from '@/api/search';
import Filters from '../filters/Filters';
import { useTranslations } from '@/lib/locale-context';
import { useDebounce } from '@/hooks/useDebounce';
import { ITEMS_PER_PAGE } from './const';
import { SearchResultsProps } from './types';

const typePriority = ['artikel', 'flash-talk', 'podcast', 'news', 'edu-talk'];

const SearchResults = ({
  className,
  query,
  setQuery,
  selectedCategories,
  selectedTypes,
  setSelectedCategories,
  setSelectedTypes,
  onClose,
  setIsExpanded,
  isOpen,
}: SearchResultsProps) => {
  const [data, setData] = useState<any[]>([]);
  const [results, setResults] = useState<any[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedQuery = useDebounce(query, 400);

  const { t, locale } = useTranslations();

  useEffect(() => {
    const trimmedQuery = debouncedQuery.trim();

    if (trimmedQuery.length < 3) {
      setData([]);
      setResults([]);
      setSelectedTypes([]);
      setSelectedCategories([]);
      return;
    }

    const fetch = async () => {
      const result = await fetchSearchData(trimmedQuery, locale);
      setData(result);
    };

    fetch();
  }, [debouncedQuery, locale, setSelectedCategories, setSelectedTypes]);

  //add "meta.description" to search in description
  const fuse = useMemo(
    () =>
      new Fuse(data, {
        keys: ['title', 'categories.variant', 'type'],
        threshold: 0.2,
      }),
    [data],
  );

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 150); // adjust if needed
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    // Avoid filtering if query is empty
    const trimmedQuery = debouncedQuery.trim();

    // Skip search if query is too short
    if (trimmedQuery.length < 3) {
      setResults([]);
      return;
    }

    let filteredResults = data;
    // Apply search query
    filteredResults = fuse.search(trimmedQuery).map((result) => result.item);

    // Apply category filter
    if (selectedCategories.length > 0) {
      filteredResults = filteredResults.filter(
        (item) =>
          item.categories &&
          Array.isArray(item.categories) &&
          item.categories.some((category: any) =>
            selectedCategories.includes(category.title),
          ),
      );
    }

    // Apply type filter
    if (selectedTypes.length > 0) {
      filteredResults = filteredResults.filter(
        (item) => item.type && selectedTypes.includes(item.type),
      );
    }

    // If 'all' is selected (or no filters applied), sort by priority
    if (
      selectedTypes.length === 0 ||
      (selectedTypes.length === 1 && selectedTypes[0] === 'all')
    ) {
      filteredResults.sort((a, b) => {
        const aIndex = typePriority.indexOf(a.type);
        const bIndex = typePriority.indexOf(b.type);
        return aIndex - bIndex;
      });
    }
    setResults(filteredResults);
  }, [debouncedQuery, data, fuse, selectedCategories, selectedTypes]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  const defaultSuggestions = [
    {
      id: 'recht',
      title: 'Recht',
      slug: 'recht',
      type: 'ratgeber',
      isSuggestion: true,
    },
    {
      id: 'anlagestrategien',
      title: 'Anlagestrategien',
      slug: 'anlagestrategien',
      type: 'ratgeber',
      isSuggestion: true,
    },
    {
      id: 'kapitalanlage',
      title: 'Kapitalanlage',
      slug: 'kapitalanlage',
      type: 'ratgeber',
      isSuggestion: true,
    },
  ];

  useEffect(() => {
    if (query.trim().length > 0) {
      setIsExpanded(true);
    } else {
      setIsExpanded(false);
    }
  }, [query, setIsExpanded]);

  //On Escape erase search input value
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setQuery('');
        if (typeof onClose === 'function') onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setQuery, onClose]);

  return (
    <div className={`${className} flex h-full flex-col justify-between`}>
      <section
        className={`flex h-full flex-col ${
          query.trim() === '' || (query.trim() !== '' && results.length === 0)
            ? 'justify-between'
            : ''
        } ${results.length === ITEMS_PER_PAGE ? 'pb-6' : ''}`}
      >
        <div className="flex flex-col items-start justify-between bg-white transition-all duration-300 ease-in-out">
          <section className="flex w-full border-b-[1px] px-[22px] py-[12px]">
            <Button
              leftIcon={{ name: 'search', color: '#07314C' }}
              variant="tertiary"
            />
            <input
              ref={inputRef}
              autoFocus
              type="text"
              placeholder={t('search.inputPlaceholder')}
              value={query}
              onChange={handleSearch}
              className="w-full focus:outline-none focus:ring-0"
            />
            <div className="hidden w-[200px] cursor-pointer items-center justify-end text-neutral-300 md:flex">
              <span className="rounded-[7px] bg-neutral-25 px-[6px] py-[4px]">
                esc
              </span>{' '}
              {t('search.escClose')}
            </div>

            <div
              className="close-icon-mobile my-auto ml-2 mr-2 block cursor-pointer rounded-full bg-neutral-300 p-1 md:hidden"
              onClick={() => {
                setQuery('');
                if (typeof onClose === 'function') {
                  onClose();
                }
              }}
            >
              <Icon
                name="x"
                size={15}
                aria-label="Clear search"
                className=""
                color="white"
                onClick={() => {
                  setQuery('');
                  if (typeof onClose === 'function') {
                    onClose();
                  }
                }}
              />
            </div>
          </section>

          <Filters
            className={`${query.trim().length >= 3 ? 'flex' : 'hidden'} w-full overflow-x-auto overflow-y-hidden whitespace-nowrap px-6`}
            selectedCategories={selectedCategories}
            setSelectedCategories={setSelectedCategories}
            selectedTypes={selectedTypes}
            setSelectedTypes={setSelectedTypes}
            border={true}
          />

          {selectedTypes.length > 0 && selectedTypes[0] !== 'all' && (
            <h3 className="pb-4 pl-6 text-start text-xs font-medium uppercase text-neutral-500">
              {selectedTypes.join(', ')}
            </h3>
          )}
        </div>

        {results.length === 0 && query.trim().length >= 3 && (
          <div className="mt-6 text-center text-lg font-[450] text-neutral-400">
            {t('search.noResults')} “
            <b className="!font-[450] text-black">{query}</b>”
          </div>
        )}
        {query.trim().length > 0 && query.trim().length < 3 && (
          <div>
            <div className="mt-6 text-center text-lg font-[450] text-neutral-400">
              {t('search.noResults')} “
              <b className="!font-[450] text-black">{query}</b>”
            </div>
            <div className="mx-auto mt-2 w-fit rounded-md bg-neutral-25 px-[6px] text-center text-sm font-normal text-neutral-400">
              {t('search.minCharacters')}
            </div>
          </div>
        )}

        <ul
          className={`flex max-h-[75%] flex-col gap-2 overflow-y-auto px-5 pb-10 md:px-6 ${results.length > 0 && 'mb-6'}`}
        >
          {results.length === 0 && (
            <div className="text-md mt-6 font-normal text-neutral-500 md:text-base">
              {t('search.defaultSuggestionsLabel')}
            </div>
          )}

          {(query.trim() === '' || (query.trim() !== '' && results.length === 0)
            ? defaultSuggestions
            : results
          ).map((item) => {
            const slugPath = getContentHubPath(locale, item.type, item.slug);
            const breadcrumbPaths = [
              { title: 'Home', slug: '/' },
              ...(item.categories
                ?.filter((category: any) => category.slug)
                .map((category: any) => ({
                  title: category.title,
                  slug: `/category/${category.slug}`,
                })) || []),
              { title: item.title, slug: slugPath },
            ];

            return (
              <li
                key={`${item.id}-${item.type}`}
                onClick={(e) => {
                  if (item.isSuggestion) {
                    e.preventDefault(); // Prevent navigation
                    setQuery(item.title); // Trigger actual Fuse search
                  }
                }}
                className={item.isSuggestion ? 'cursor-pointer' : ''}
              >
                <a
                  href={item.isSuggestion ? '#' : slugPath}
                  aria-label={item.isSuggestion ? '#' : slugPath}
                >
                  <Card
                    variant="search"
                    title={item.title}
                    link={{ href: slugPath, title: 'Read more' }}
                    breadcrumbPaths={breadcrumbPaths}
                    query={item.isSuggestion ? '' : query} // 👈 prevent bolding in default suggestions
                    {...(item.isSuggestion
                      ? {
                          description: '',
                          type: '',
                          date: '',
                          category: '',
                          variantBadge: '',
                        }
                      : {
                          type: item.type,
                          date: item.publishedAt
                            ? new Date(item.publishedAt).toLocaleDateString(
                                'de-DE',
                              )
                            : '',
                          category: item.categories?.[0]?.title || '',
                          description: item.meta?.description || '',
                          variantBadge:
                            item.categories?.[0]?.variant || 'cream',
                        })}
                  />
                </a>
              </li>
            );
          })}
        </ul>
      </section>

      {results.length > 0 && query.trim() !== '' && (
        <div className="absolute bottom-0 left-1/2 w-full -translate-x-1/2 rounded-b-2xl border-t-[1px] bg-white pb-3 pl-4 pt-3 text-start text-[15px] font-normal text-neutral-500">
          {results.length}{' '}
          {results.length === 1
            ? t('search.resultFound')
            : t('search.resultsFound')}
        </div>
      )}
    </div>
  );
};

export default SearchResults;
